/**
 * The two actions the launch footer offers, for whichever run-settings
 * dialog is showing it (#2175 slice 2).
 *
 * All three dialogs need the same pair — run with what is on screen, or
 * save first and then run — and the same in-flight guard around them. The
 * dialogs differ in what they save and what they collect; they do not
 * differ in this, so it lives once.
 *
 * The guard is a ref rather than the busy flag: a second click lands before
 * React has applied the state update from the first, so reading state to
 * decide would start two runs. Same reason `useLiveRun` keeps one.
 */
import { useCallback, useRef, useState } from 'react'

export interface RunTemplateLaunch {
  /** True while a save or a start is in flight. Disables the footer. */
  busy: boolean
  /** Start with what is on screen. Nothing is saved. */
  runNow: () => Promise<void>
  /** Save what is on screen, then start with it. */
  saveAndRun: () => Promise<void>
}

export function useRunTemplateLaunch(opts: {
  /** Collect the form as the template's stored shape. */
  buildSettings: () => Record<string, unknown>
  /** Persist the form. Resolves `true` when the write landed, `false`
   * when it failed — the caller has already told the operator why.
   * Save and run does NOT run on `false`: starting a run after a failed
   * save would use the old saved values while the screen shows the new
   * ones, which is the most confusing outcome available. */
  save: () => Promise<boolean>
  /** Start the run. `settings` present means "for this run only". */
  launch: (settings?: Record<string, unknown>) => Promise<void> | void
}): RunTemplateLaunch {
  const { buildSettings, save, launch } = opts
  const [busy, setBusy] = useState(false)
  const inFlight = useRef(false)

  const run = useCallback(
    async (saveFirst: boolean) => {
      if (inFlight.current) return
      inFlight.current = true
      setBusy(true)
      try {
        if (saveFirst) {
          if (!(await save())) return
          // Saved values ARE the template now, so the run needs no
          // override — sending one would be the same values twice, and
          // the second copy would be the one that goes stale.
          await launch()
        } else {
          await launch(buildSettings())
        }
      } finally {
        inFlight.current = false
        setBusy(false)
      }
    },
    [buildSettings, save, launch],
  )

  return {
    busy,
    runNow: useCallback(() => run(false), [run]),
    saveAndRun: useCallback(() => run(true), [run]),
  }
}
